import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { groupBy } from 'lodash';

import AlertBanner from '~/components/core/AlertBanner';
import FsButton from '~/components/core/FsWrappers/FsButton/FsButton';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import SafeComponent, { CriticalityLevel } from '~/components/core/SafeComponent';
import Text from '~/components/core/TextComponents/Text';
import type {
  VitesseProgrammeCheckArguments,
  VitesseProgrammeCheckResult,
  VitesseProgrammeCheckResultVitessePassphraseMethodOption,
  VitesseProgrammeCheckResultVitesseProgrammeIssue,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

type IssueSeverity = VitesseProgrammeCheckResultVitesseProgrammeIssue['severity'];
type AlertType = React.ComponentProps<typeof AlertBanner>['alertType'];

// The verdict answers the question the button asked - pass or fail - before any detail. Info-only
// findings (programme settings, the passphrase rule) are facts about the programme, not problems, so
// they must not suppress a passing verdict.
const VERDICTS: Record<'error' | 'warning' | 'ok', { title: string; note: string; alertType: AlertType }> = {
  error: {
    title: 'Not valid for this programme',
    note: 'Payments using this configuration would fail. Fix the blocking issues below.',
    alertType: AlertBanner.ALERT_TYPES.ERROR,
  },
  warning: {
    title: 'Valid, with warnings',
    note: 'Payments would go through, but check the warnings below before saving.',
    alertType: AlertBanner.ALERT_TYPES.WARNING,
  },
  ok: {
    title: 'Configuration is valid for this programme',
    note: 'Every check passed against the programme Vitesse returned.',
    alertType: AlertBanner.ALERT_TYPES.DONE,
  },
};

// `info` is deliberately absent: it is rendered under its own heading below the verdict.
const DETAIL_SEVERITIES: { severity: IssueSeverity; title: string; alertType: AlertType }[] = [
  { severity: 'error', title: 'Blocking issues', alertType: AlertBanner.ALERT_TYPES.ERROR },
  { severity: 'warning', title: 'Warnings', alertType: AlertBanner.ALERT_TYPES.WARNING },
  { severity: 'info', title: 'What the payee will experience', alertType: AlertBanner.ALERT_TYPES.INFO },
];

export interface VitesseProgrammeVerificationProps {
  organizationId: number | string;
  config: VitesseProgrammeCheckArguments;
  disabled?: boolean;
  /** The programme's passphrase definitions, so the form can offer them instead of a pasted GUID. */
  onPassphraseMethodsLoaded?: (methods: VitesseProgrammeCheckResultVitessePassphraseMethodOption[]) => void;
}

const IssueList: React.FC<{ issues: VitesseProgrammeCheckResultVitesseProgrammeIssue[] }> = ({ issues }) => (
  <ul className="m-0 flex flex-col gap-4 pl-20">
    {issues.map((issue, index) => (
      <li key={`${issue.code}-${index}`}>
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
          {issue.message}
        </Text>
      </li>
    ))}
  </ul>
);

const VitesseProgrammeVerification: React.FC<VitesseProgrammeVerificationProps> = ({
  organizationId,
  config,
  disabled = false,
  onPassphraseMethodsLoaded,
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [issues, setIssues] = useState<VitesseProgrammeCheckResultVitesseProgrammeIssue[] | null>(null);
  // The config the displayed result was produced from. Anything else on screen means the result is
  // stale and must stop asserting a verdict that no longer describes the form.
  const [verifiedConfigKey, setVerifiedConfigKey] = useState<string | null>(null);

  const configKey = JSON.stringify(config);
  const isStale = issues !== null && verifiedConfigKey !== configKey;

  const handleVerify = useCallback(async () => {
    setIsVerifying(true);
    try {
      const { data } = await axios.post<VitesseProgrammeCheckResult>(
        `/sysconfig/api/v1/organizations/${organizationId}/vitesse/verify_programme`,
        JSON.parse(configKey)
      );
      setIssues(data.issues);
      setVerifiedConfigKey(configKey);
      onPassphraseMethodsLoaded?.(data.passphrase_methods ?? []);
    } catch (error) {
      // A 422 is the endpoint's handled precondition failure - `VitesseNotConfiguredError` ("Vitesse is
      // not configured for this organization"), the one message here that tells the user what to do.
      // `extractError` has no case for a message without a `reasons` list, so letting it through
      // `reportAxiosError` would show the generic "an unexpected error has occurred" dialog instead.
      // Surfaced as an error finding, which is how the endpoint already reports an unreachable programme.
      const handledMessage =
        axios.isAxiosError(error) && error.response?.status === 422
          ? (error.response.data as { message?: string } | undefined)?.message
          : undefined;
      if (handledMessage) {
        setIssues([{ severity: 'error', code: 'vitesse_not_configured', message: handledMessage }]);
        setVerifiedConfigKey(configKey);
      } else {
        setIssues(null);
        setVerifiedConfigKey(null);
        await reportAxiosError(error);
      }
    } finally {
      setIsVerifying(false);
    }
  }, [configKey, onPassphraseMethodsLoaded, organizationId]);

  // Deliberately click-only, never on mount: this calls a payment provider, and opening the payments
  // dialog to edit something unrelated should not hit Vitesse, greet the user with a verdict about the
  // passphrase, or throw an error toast when Vitesse is unavailable. Problems on a *saved* config are
  // already caught by the send-time guards, which run the same check and raise a claim reminder.
  const issuesBySeverity = groupBy(issues ?? [], 'severity');
  const verdict = issuesBySeverity.error?.length
    ? VERDICTS.error
    : issuesBySeverity.warning?.length
      ? VERDICTS.warning
      : VERDICTS.ok;

  return (
    <SafeComponent
      location="SystemConfiguration.PaymentsConfiguration.VitesseProgrammeVerification"
      criticalityLevel={CriticalityLevel.MEDIUM}
    >
      <div className="flex flex-col gap-12">
        <div>
          <FsButton color="primary" variant="contained" disabled={disabled || isVerifying} onClick={handleVerify}>
            {issues ? 'Re-check against Vitesse' : 'Check against Vitesse'}
          </FsButton>
        </div>
        <LoadingSwitch isLoading={isVerifying}>
          {issues ? (
            <div className="flex flex-col gap-8">
              {isStale ? (
                <AlertBanner
                  alertType={AlertBanner.ALERT_TYPES.WARNING}
                  title="Configuration changed"
                  note="These results describe the previous values. Check again to see whether the current ones are valid."
                  withIcon
                />
              ) : (
                <>
                  <AlertBanner alertType={verdict.alertType} title={verdict.title} note={verdict.note} withIcon />
                  {DETAIL_SEVERITIES.map(({ severity, title, alertType }) =>
                    issuesBySeverity[severity]?.length ? (
                      <AlertBanner
                        key={severity}
                        alertType={alertType}
                        title={title}
                        note={<IssueList issues={issuesBySeverity[severity]} />}
                        withIcon
                      />
                    ) : null
                  )}
                </>
              )}
            </div>
          ) : null}
        </LoadingSwitch>
      </div>
    </SafeComponent>
  );
};

export default VitesseProgrammeVerification;
